import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SketchButton } from './sketchy';
import CassetteLoader from './CassetteLoader';
import { useAuth } from '../contexts/AuthContext';
import { displayPriceForTier, priceIdForTier, type PaidTier } from '../constants/stripePrices';
import { createCheckoutSession, createUpgradeSession } from '../services/stripeService';
import { logError, logInfo } from '../services/logtail';
import ga from '../services/analytics';
import { storageSettingsSectionPath } from '../services/storageService';

interface CassetteCloudPaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CassetteCloudPaywallModal: React.FC<CassetteCloudPaywallModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userType, isAnonymous, linkWithGoogle } = useAuth();
  const [loadingTier, setLoadingTier] = useState<PaidTier | null>(null);

  if (!isOpen) return null;

  const handleSubscribe = async (tier: PaidTier) => {
    if (isAnonymous) {
      onClose();
      void linkWithGoogle();
      return;
    }
    setLoadingTier(tier);
    try {
      const priceId = priceIdForTier(tier, true);
      if (!priceId) {
        throw new Error(`Stripe price ID for ${tier}+cloud not configured`);
      }
      logInfo('Cassette Cloud paywall checkout', { tier, userType });
      ga.event('cassette_cloud_paywall_subscribe', { tier, from_tier: userType });

      const response =
        userType === 'artist' || userType === 'studio'
          ? await createUpgradeSession(priceId)
          : await createCheckoutSession(priceId);

      if (response.success && response.sessionUrl) {
        window.location.href = response.sessionUrl;
        return;
      }
      throw new Error(response.errorMessage || 'Failed to create session');
    } catch (error) {
      logError('Cassette Cloud paywall checkout failed', { error, tier, userType });
      alert(t('settings.storage.cassetteCloudPaywallCheckoutFailed', 'Could not start checkout. Try again.'));
    } finally {
      setLoadingTier(null);
    }
  };

  const artistPrice = displayPriceForTier('artist', true);
  const studioPrice = displayPriceForTier('studio', true);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      data-testid="cassette-cloud-paywall"
    >
      <div
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-lg p-6 max-w-md w-full"
        role="dialog"
        aria-labelledby="cassette-cloud-paywall-title"
      >
        <h2 id="cassette-cloud-paywall-title" className="text-xl font-bold text-[var(--color-text)] mb-2">
          {t('settings.storage.cassetteCloudPaywallTitle', 'Cassette Cloud requires a cloud plan')}
        </h2>
        <p className="text-sm text-[var(--color-muted)] mb-4">
          {t(
            'settings.storage.cassetteCloudPaywallBody',
            'Project storage on Cassette Cloud is included with Artist + Cassette Cloud or Studio + Cassette Cloud. Link your personal cloud instead, or upgrade to a cloud plan.',
          )}
        </p>

        <SketchButton
          className="w-full mb-3"
          onClick={() => {
            onClose();
            navigate(storageSettingsSectionPath());
          }}
          data-testid="cassette-paywall-link-cloud"
        >
          {t('settings.storage.cassetteCloudPaywallLinkCloud', 'Link personal cloud')}
        </SketchButton>

        <div className="space-y-3">
          <SketchButton
            className="w-full flex flex-col items-start gap-1 py-3"
            disabled={loadingTier !== null}
            onClick={() => void handleSubscribe('artist')}
            data-testid="cassette-paywall-artist"
          >
            <span className="font-semibold">
              {loadingTier === 'artist'
                ? t('upgrade.processing', 'Processing…')
                : t('settings.storage.cassetteCloudPaywallArtist', {
                    price: artistPrice,
                    defaultValue: 'Artist + Cassette Cloud — ${{price}}/month',
                  })}
            </span>
            <span className="text-xs opacity-90">
              {t('upgrade.artistDescription', '100 tracks, 10 collaborators')}
            </span>
          </SketchButton>

          <SketchButton
            className="w-full flex flex-col items-start gap-1 py-3"
            disabled={loadingTier !== null}
            onClick={() => void handleSubscribe('studio')}
            data-testid="cassette-paywall-studio"
          >
            <span className="font-semibold">
              {loadingTier === 'studio'
                ? t('upgrade.processing', 'Processing…')
                : t('settings.storage.cassetteCloudPaywallStudio', {
                    price: studioPrice,
                    defaultValue: 'Studio + Cassette Cloud — ${{price}}/month',
                  })}
            </span>
            <span className="text-xs opacity-90">
              {t('upgrade.studioDescription', 'Unlimited tracks and collaborators')}
            </span>
          </SketchButton>
        </div>

        {loadingTier && (
          <div className="flex justify-center mt-4">
            <CassetteLoader size={32} variant="inline" />
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2">
          <a
            href="/upgrade"
            className="w-full py-2 text-sm text-[var(--brand-from)] underline text-center"
            onClick={() => onClose()}
          >
            {t('settings.storage.cassetteCloudPaywallCompare', 'Compare all plans')}
          </a>
          <SketchButton variant="ghost" onClick={onClose} className="w-full">
            {t('buttons.cancel')}
          </SketchButton>
        </div>
      </div>
    </div>
  );
};

export default CassetteCloudPaywallModal;
